
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export const syncProductDetailsInGroup = async (
  contentId: string | null,
  productDetails: string | null,
  collectionId: string
): Promise<boolean> => {
  if (!contentId) return false;

  try {
    console.log(`Syncing product details for group ${contentId}`);
    
    const { data: groupProducts, error: groupError } = await supabase
      .from("product_descriptions")
      .select("id")
      .eq("content_id", contentId)
      .eq("collection_id", collectionId);

    if (groupError) throw groupError;

    if (groupProducts && groupProducts.length > 0) {
      const { error: updateError } = await supabase
        .from("product_descriptions")
        .update({ 
          product_details: productDetails,
          updated_at: new Date().toISOString()
        })
        .in("id", groupProducts.map(p => p.id));

      if (updateError) throw updateError;

      console.log(`Updated ${groupProducts.length} products in group ${contentId}`);
      return true;
    }

    return false;
  } catch (error) {
    console.error("Error syncing product details:", error);
    toast.error("Failed to update product details for all variants");
    return false;
  }
};

/**
 * Ensure descriptions are consistent across all products in a group
 */
export const syncGroupDescriptions = async (
  contentId: string | null,
  improvedDescription: string,
  collectionId: string
): Promise<boolean> => {
  if (!contentId) return false;
  
  try {
    console.log(`Syncing descriptions for group ${contentId}`);
    
    // Get all products in the group
    const { data: groupProducts, error: groupError } = await supabase
      .from("product_descriptions")
      .select("id, status")
      .eq("content_id", contentId)
      .eq("collection_id", collectionId);
      
    if (groupError) throw groupError;
    
    // If we found other products in the same group
    if (groupProducts && groupProducts.length > 0) {
      // Update all products in the group to have the same description
      // also preserve their current status
      const { error: updateError } = await supabase
        .from("product_descriptions")
        .update({ 
          improved_description: improvedDescription,
          updated_at: new Date().toISOString()
        })
        .in("id", groupProducts.map(p => p.id));
        
      if (updateError) throw updateError;
      
      console.log(`Updated descriptions for ${groupProducts.length} products in group ${contentId}`);
      toast.success(`Updated all ${groupProducts.length} product variants in this group`);
      return true;
    }
    
    return false;
  } catch (error) {
    console.error("Error syncing group descriptions:", error);
    toast.error("Failed to update descriptions for all variants");
    return false;
  }
};

/**
 * Verify and fix any inconsistent descriptions within product groups
 */
export const validateGroupConsistency = async (
  collectionId: string
): Promise<{fixed: number, groups: number}> => {
  try {
    console.log(`Validating group consistency for collection ${collectionId}`);
    
    // Get all content groups in this collection
    const { data: productsWithContentIds, error: groupsError } = await supabase
      .from("product_descriptions")
      .select("content_id")
      .eq("collection_id", collectionId)
      .not("content_id", "is", null);
    
    if (groupsError) throw groupsError;
    
    if (!productsWithContentIds || productsWithContentIds.length === 0) {
      console.log("No content groups found in collection");
      return { fixed: 0, groups: 0 };
    }
    
    // Extract unique content IDs
    const contentIds = Array.from(new Set(productsWithContentIds.map(item => item.content_id)));
    
    let fixedCount = 0;
    let inconsistentGroups = 0;
    
    // For each group, find inconsistencies
    for (const contentId of contentIds) {
      if (!contentId) continue;
      
      // Get all products in this group
      const { data: groupProducts, error: productsError } = await supabase
        .from("product_descriptions")
        .select("id, improved_description, generated_description, product_details")
        .eq("content_id", contentId)
        .eq("collection_id", collectionId);
      
      if (productsError) {
        console.error(`Error fetching products for group ${contentId}:`, productsError);
        continue;
      }
      
      if (!groupProducts || groupProducts.length <= 1) continue;
      
      // Find descriptions and comprehensive product details for validation
      const descriptions = groupProducts.map(p => p.improved_description || p.generated_description);
      const productDetails = groupProducts.map(p => p.product_details || '');
      
      console.log(`📋 Group ${contentId} validation: ${groupProducts.length} products`);
      console.log(`   Descriptions: ${descriptions.map(d => d?.substring(0, 50) + '...' || 'empty').join(' | ')}`);
      console.log(`   Product details: ${productDetails.map(d => d?.substring(0, 30) + '...' || 'empty').join(' | ')}`);
      
      // Check if all descriptions are the same
      const uniqueDescriptions = new Set(descriptions.filter(d => d && d.trim()));
      if (uniqueDescriptions.size > 1) {
        // Found inconsistency
        inconsistentGroups++;
        console.log(`⚠️ Found inconsistent descriptions in group ${contentId}`);
        
        // Use the first non-empty description as the canonical one
        const canonicalDescription = descriptions.find(d => d && d.trim().length > 0);
        if (canonicalDescription) {
          console.log(`🔧 Syncing descriptions for group ${contentId} with canonical: "${canonicalDescription.substring(0, 100)}..."`);
          // Sync descriptions
          await syncGroupDescriptions(contentId, canonicalDescription, collectionId);
          fixedCount += groupProducts.length;
        }
      } else {
        console.log(`✅ Group ${contentId} has consistent descriptions`);
      }
    }
    
    console.log(`Found ${inconsistentGroups} inconsistent groups, fixed ${fixedCount} products`);
    return { fixed: fixedCount, groups: inconsistentGroups };
  } catch (error) {
    console.error("Error validating group consistency:", error);
    return { fixed: 0, groups: 0 };
  }
};
